import { useCallback, useRef } from 'react';
import { useLazyCommand, useLazyQuery } from '@soap/modules';

export function useLessonsPageCommands() {
    const [sendGetUnitsCommandRaw] = useLazyCommand('C251v1_GetUnits');
    /** At most one GetUnits in flight; further calls remember the latest student id. */
    const getUnitsInFlightRef = useRef(false);
    const getUnitsPendingStudentIdRef = useRef<string | null>(null);
    /** Monotonic request id for the send currently (or next) in flight. */
    const getUnitsRequestEpochRef = useRef(0);
    const getUnitsLatestSentEpochRef = useRef(0);
    const getUnitsInFlightRequestEpochRef = useRef(0);
    /**
     * Bumped on every local units patch. In-flight GetUnits responses are ignored when the
     * content epoch moved after that request was sent (patch won the race).
     */
    const unitsContentEpochRef = useRef(0);
    const getUnitsInFlightContentEpochRef = useRef(0);

    const noteUnitsLocalMutation = useCallback(() => {
        unitsContentEpochRef.current += 1;
    }, []);

    const sendGetUnitsCommand = useCallback(
        (args: { C251_StudentId: string }) => {
            if (!args?.C251_StudentId) return;
            if (getUnitsInFlightRef.current) {
                getUnitsPendingStudentIdRef.current = args.C251_StudentId;
                return;
            }
            getUnitsInFlightRef.current = true;
            getUnitsPendingStudentIdRef.current = null;
            const epoch = getUnitsRequestEpochRef.current + 1;
            getUnitsRequestEpochRef.current = epoch;
            getUnitsLatestSentEpochRef.current = epoch;
            getUnitsInFlightRequestEpochRef.current = epoch;
            getUnitsInFlightContentEpochRef.current = unitsContentEpochRef.current;
            sendGetUnitsCommandRaw(args);
        },
        [sendGetUnitsCommandRaw]
    );

    /**
     * Call when E251v1_GotUnits arrives. Returns whether the payload should replace units.
     * Always drains coalesce / clears in-flight so follow-ups can send.
     */
    const consumeGetUnitsResponse = useCallback((): { shouldApply: boolean } => {
        const shouldApply =
            getUnitsInFlightRequestEpochRef.current === getUnitsLatestSentEpochRef.current &&
            getUnitsInFlightContentEpochRef.current === unitsContentEpochRef.current;

        const pendingStudentId = getUnitsPendingStudentIdRef.current;
        getUnitsPendingStudentIdRef.current = null;
        if (pendingStudentId) {
            const epoch = getUnitsRequestEpochRef.current + 1;
            getUnitsRequestEpochRef.current = epoch;
            getUnitsLatestSentEpochRef.current = epoch;
            getUnitsInFlightRequestEpochRef.current = epoch;
            getUnitsInFlightContentEpochRef.current = unitsContentEpochRef.current;
            // Keep in-flight true across the follow-up send
            sendGetUnitsCommandRaw({ C251_StudentId: pendingStudentId });
            return { shouldApply };
        }
        getUnitsInFlightRef.current = false;
        return { shouldApply };
    }, [sendGetUnitsCommandRaw]);

    /** @deprecated Prefer consumeGetUnitsResponse — kept name for call-site clarity during migration. */
    const notifyGetUnitsResponse = useCallback(() => {
        consumeGetUnitsResponse();
    }, [consumeGetUnitsResponse]);

    const [sendCreateLessonCommand] = useLazyCommand('C250v1_CreateUnit');
    const [sendCurriculumCommand] = useLazyCommand('C258v1_GetCurriculumStructure');
    const [sendGetCurriculumNodeChildrenCommand] = useLazyCommand('C285v1_GetCurriculumNodeChildren');
    const [sendGetTopicDetailsCommand] = useLazyCommand('C286v1_GetTopicDetails');
    const [sendGetStudentCurriculumCommand] = useLazyCommand('C260v1_GetStudentCurriculum');
    const [sendGetStudentCurriculumNodeChildrenCommand] = useLazyCommand('C287v1_GetStudentCurriculumNodeChildren');
    const [sendAddTopicsCommand] = useLazyCommand('C259v1_AddTopicsToStudentCurriculum');
    const [sendEnsureCustomSubjectInTenantCurriculumCommand] = useLazyCommand(
        'C316v1_EnsureCustomSubjectInTenantCustomCurriculum'
    );
    const [sendGetCustomSubjectTopicNamesCommand] = useLazyCommand('C297v1_GetCustomSubjectTopicNames');
    const [sendAddCustomSubjectTopicsBatchCommand] = useLazyCommand('C298v1_AddCustomSubjectTopicsBatchToTenantCustomCurriculum');
    const [sendCreateAssessmentCommand] = useLazyCommand('C274v1_CreateAssessment');
    const [sendRemoveTopicsCommand] = useLazyCommand('C261v1_RemoveTopicsFromStudentCurriculum');
    const [sendApproveUnitCommand] = useLazyCommand('C264v1_ApproveUnit');
    const [sendDeleteUnitCommand] = useLazyCommand('C265v1_DeleteUnit');
    const [sendRegenerateUnitCommand] = useLazyCommand('C266v1_RegenerateUnit');
    const [sendStartLessonCommand] = useLazyCommand('C268v1_StartLesson');
    const [sendMarkUnitTouchedCommand] = useLazyCommand('C281v1_MarkUnitTouched');
    const [sendReorderTopicsCommand] = useLazyCommand('C288v1_ReorderTopicsInStudentCurriculum');
    const [sendUpdateTopicMinHoursCommand] = useLazyCommand('C289v1_UpdateTopicMinHours');
    const [sendGetTenantCustomCurriculumCommand] = useLazyCommand('C308v1_GetTenantCustomCurriculum');
    const [sendAddTenantCustomCurriculumTopicCommand] = useLazyCommand('C309v1_AddTenantCustomCurriculumTopic');
    const [sendCopyTenantCustomCurriculumTopicsCommand] = useLazyCommand('C310v1_CopyTenantCustomCatalogTopicsToStudentCurriculum');
    const [sendDeleteTenantCustomCurriculumTopicsCommand] = useLazyCommand('C317v1_DeleteTenantCustomCurriculumTopics');
    const [sendGetEducationSystemsCommand] = useLazyCommand('C311v1_GetEducationSystems');
    const [sendAddTenantEducationSystemCommand] = useLazyCommand('C312v1_AddTenantEducationSystem');
    const [sendGetEducationSystemDefinitionsCommand] = useLazyCommand('C313v1_GetEducationSystemDefinitions');
    const [sendAddCustomTopicToTenantCurriculumCommand] = useLazyCommand(
        'C262v1_AddCustomTopicToTenantCustomCurriculum'
    );
    const [getAddCustomTopicFormData] = useLazyQuery('C263v1_GetAddCustomTopicFormData');

    return {
        sendGetUnitsCommand,
        consumeGetUnitsResponse,
        notifyGetUnitsResponse,
        noteUnitsLocalMutation,
        sendCreateLessonCommand,
        sendCurriculumCommand,
        sendGetCurriculumNodeChildrenCommand,
        sendGetTopicDetailsCommand,
        sendGetStudentCurriculumCommand,
        sendGetStudentCurriculumNodeChildrenCommand,
        sendAddTopicsCommand,
        sendEnsureCustomSubjectInTenantCurriculumCommand,
        sendGetCustomSubjectTopicNamesCommand,
        sendAddCustomSubjectTopicsBatchCommand,
        sendCreateAssessmentCommand,
        sendRemoveTopicsCommand,
        sendApproveUnitCommand,
        sendDeleteUnitCommand,
        sendRegenerateUnitCommand,
        sendStartLessonCommand,
        sendMarkUnitTouchedCommand,
        sendReorderTopicsCommand,
        sendUpdateTopicMinHoursCommand,
        sendGetTenantCustomCurriculumCommand,
        sendAddTenantCustomCurriculumTopicCommand,
        sendCopyTenantCustomCurriculumTopicsCommand,
        sendDeleteTenantCustomCurriculumTopicsCommand,
        sendGetEducationSystemsCommand,
        sendAddTenantEducationSystemCommand,
        sendGetEducationSystemDefinitionsCommand,
        sendAddCustomTopicToTenantCurriculumCommand,
        getAddCustomTopicFormData
    };
}
